import React, { useEffect, useState, useCallback } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { RefreshCw, Users, UserCheck, MapPin } from 'lucide-react';
import { getPartnerAnalytics } from '../../../constant/Constant';
import { HitApi } from '../../../Api/ApiHit';

const GOOGLE_MAPS_API_KEY = 'AIzaSyAa6bVCnlEiyID9ZvGeIMYKWQyQDQZlD7M';

const MAP_CONTAINER_STYLE = {
  width: '100%',
  height: '100%',
  borderRadius: '12px',
};

const DEFAULT_CENTER = {
  lat: 28.6139,
  lng: 77.2090,
};

const PartnerMap = () => {
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [filter, setFilter] = useState('online');
  const [stats, setStats] = useState({ total: 0, online: 0 });

  const fetchPartners = useCallback(() => {
    setLoading(true);
    HitApi({
      page: 1,
      limit: 1000,
      filter: filter,
    }, getPartnerAnalytics).then((result) => {
      if (result.success) {
        setStats({
          total: result.data.totalPartners,
          online: result.data.onlinePartners,
        });
        const docs = result.data.partners?.docs || [];
        // Filter partners with valid coordinates
        const withLocation = docs.filter(
          (p) => p.location?.coordinates?.[0] && p.location?.coordinates?.[1] &&
                 (p.location.coordinates[0] !== 0 || p.location.coordinates[1] !== 0)
        );
        setPartners(withLocation);
      }
    }).catch((error) => {
      console.error('Error fetching partners for map:', error);
    }).finally(() => {
      setLoading(false);
    });
  }, [filter]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const getCenter = () => {
    if (partners.length > 0) {
      return {
        lat: partners[0].location.coordinates[1],
        lng: partners[0].location.coordinates[0],
      };
    }
    return DEFAULT_CENTER;
  };

  return (
    <div className="p-6 h-screen flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Partner Map</h1>
          <p className="text-sm text-gray-500 mt-1">View live partner locations</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Stats */}
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
            <Users className="w-4 h-4 text-gray-500" />
            <span className="text-xs text-gray-600 font-medium">{stats.total} Total</span>
          </div>
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            <UserCheck className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-700 font-medium">{stats.online} Online</span>
          </div>
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
            <MapPin className="w-4 h-4 text-gray-500" />
            <span className="text-xs text-gray-600 font-medium">{partners.length} On Map</span>
          </div>

          {/* Filter */}
          <div className="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden">
            {['all', 'online', 'offline'].map((opt) => (
              <button
                key={opt}
                onClick={() => setFilter(opt)}
                className={`px-3 py-2 text-xs font-medium capitalize transition-colors ${
                  filter === opt
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>

          <button
            onClick={fetchPartners}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 bg-white rounded-xl shadow border border-gray-100 overflow-hidden min-h-0">
        <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
          <GoogleMap
            mapContainerStyle={MAP_CONTAINER_STYLE}
            center={getCenter()}
            zoom={12}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: true,
            }}
          >
            {partners.map((partner) => (
              <Marker
                key={partner._id}
                position={{
                  lat: partner.location.coordinates[1],
                  lng: partner.location.coordinates[0],
                }}
                onClick={() => setSelectedPartner(partner)}
                icon={{
                  url: partner.isOnline
                    ? 'https://maps.google.com/mapfiles/ms/icons/green-dot.png'
                    : 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                }}
              />
            ))}

            {selectedPartner && (
              <InfoWindow
                position={{
                  lat: selectedPartner.location.coordinates[1],
                  lng: selectedPartner.location.coordinates[0],
                }}
                onCloseClick={() => setSelectedPartner(null)}
              >
                <div style={{ minWidth: 180, padding: 4 }}>
                  <h3 style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                    {selectedPartner.name || 'Unknown'}
                  </h3>
                  <p style={{ fontSize: 12, color: '#666', margin: '2px 0' }}>
                    {selectedPartner.phoneNumber}
                  </p>
                  {selectedPartner.email && (
                    <p style={{ fontSize: 12, color: '#666', margin: '2px 0' }}>
                      {selectedPartner.email}
                    </p>
                  )}
                  <p style={{
                    fontSize: 11,
                    fontWeight: 600,
                    marginTop: 6,
                    color: selectedPartner.isOnline ? '#16a34a' : '#dc2626',
                  }}>
                    {selectedPartner.isOnline ? '● Online' : '● Offline'}
                  </p>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </LoadScript>
      </div>
    </div>
  );
};

export default PartnerMap;
